import React from "react";
import PaginationTable from "../../components/paginationTable/PaginationTable";
import {
  Comparator,
  dateFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import CamplaCoreService from "../../services/CamplaCoreService";
import Button from "react-bootstrap/Button";
import StorageService from "../../services/StorageService";
import {
  ButtonGroup,
  Col,
  Container,
  Row,
  ToggleButton,
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faPlus } from "@fortawesome/free-solid-svg-icons";
import APIErrorHelper from "../../helpers/APIErrorHelper";
import UnsavedChangesDialog from "../../components/dialogs/unsavedChangesDialog/UnsavedChangesDialog";
import { Link } from "react-router-dom";
import CreateSoftwareCatalogDialog from "./dialogs/createSoftwareCatalogDialog/CreateSoftwareCatalogDialog";
import SoftwareCatalogsActionCell from "./softwareCatalogsActionCell/SoftwareCatalogsActionCell";
import DeleteDialog from "../../components/dialogs/deleteDialog/DeleteDialog";
import DateTimeHelper from "../../helpers/DateTimeHelper";
import PaginationTableFilterToggle from "../../components/paginationTableFilterToggle/PaginationTableFilterToggle";

class SoftwareCatalogs extends React.Component {
  tableId = "softwareCatalogsTable";
  constructor(props) {
    super(props);
    let showFilters = StorageService.tableConfigShowFilters(this.tableId);
    this.state = {
      softwareCatalogs: [],
      showCreateModal: false,
      showDeleteModal: false,
      showUnsavedChangesDialog: false,
      cloneItemId: null,
      closeAction: () => {},
      catalogToDeleteOrEdit: null,
      errorMessage: "",
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      dialogApiError: "",
      apiError: "",
    };

    this.loadData = this.loadData.bind(this);
    this.loadCachedData = this.loadCachedData.bind(this);
    this.handleCreateCatalog = this.handleCreateCatalog.bind(this);
    this.handleDeleteCatalog = this.handleDeleteCatalog.bind(this);
    this.hideCreationDialog = this.hideCreationDialog.bind(this);
    this.onHideCreationDialog = this.onHideCreationDialog.bind(this);
    this.handleClone = this.handleClone.bind(this);
  }

  async componentDidMount() {
    this.paginationTableRef = React.createRef();
    this.loadCachedData();
    this.setState(await this.loadData());
  }

  async handleClone(softwareCatalog) {
    try {
      this.setState({ cloneItemId: softwareCatalog._id });
      const clonedCatalog = await CamplaCoreService.cloneSoftwareCatalog(softwareCatalog._id);
      const updatedCatalogs = [...this.state.softwareCatalogs, clonedCatalog];
      StorageService.set("softwareCatalogs", updatedCatalogs);
      this.setState({
        softwareCatalogs: updatedCatalogs,
        cloneItemId: null
      });
    } catch (err) {
      this.setState({
        cloneItemId: null,
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {},
            this.props.t
        ),
      });
    }
  }

  async handleCreateCatalog(catalogToCreate) {
    try {
      const createdCatalog = await CamplaCoreService.createSoftwareCatalog(
        catalogToCreate
      );
      const updatedCatalogs = [...this.state.softwareCatalogs, createdCatalog];
      StorageService.set("softwareCatalogs", updatedCatalogs);
      this.setState({
        showCreateModal: false,
        softwareCatalogs: updatedCatalogs,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "SoftwareCatalogs.APIError.PreconditionFailed",
          },
          this.props.t
        ),
      });
    }
  }

  async handleDeleteCatalog() {
    try {
      await CamplaCoreService.deleteSoftwareCatalog(
        this.state.catalogToDeleteOrEdit._id
      );
      const updatedCatalogs = this.state.softwareCatalogs.filter(
        (x) => x._id !== this.state.catalogToDeleteOrEdit._id
      );
      StorageService.set("softwareCatalogs", updatedCatalogs);
      this.setState({
        showDeleteModal: false,
        softwareCatalogs: updatedCatalogs,
      });
    } catch (err) {
      this.setState({
        dialogApiError: APIErrorHelper.getError(
          err,
          () => this.setState({ dialogApiError: "" }),
          {
            PRECONDITION_FAILED: "SoftwareCatalogs.APIError.PreconditionFailed",
            NOT_FOUND: "SoftwareCatalogs.APIError.NotFound",
          },
          this.props.t
        ),
      });
    }
  }

  loadCachedData() {
    const softwareCatalogs = StorageService.getReferenceType(
      "softwareCatalogs"
    );
    if (softwareCatalogs) {
      this.setState({
        softwareCatalogs,
      });
    }
  }

  async loadData() {
    try {
      const softwareCatalogs = await CamplaCoreService.getAllSoftwareCatalogs();
      StorageService.set("softwareCatalogs", softwareCatalogs);
      return {
        softwareCatalogs,
      };
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {},
          this.props.t
        ),
      });
    }
    return {
      softwareCatalogs: [],
    };
  }

  hideCreationDialog() {
    this.setState({ showCreateModal: false, dialogApiError: "" });
  }

  onHideCreationDialog(isFormDirty) {
    if (isFormDirty) {
      this.setState({
        showUnsavedChangesDialog: true,
        closeAction: this.hideCreationDialog,
      });
    } else {
      this.hideCreationDialog();
    }
  }

  render() {
    const { t } = this.props;
    const columns = [
      {
        dataField: "name",
        text: t("SoftwareCatalogs.ColumnName"),
        sort: true,
        filter: textFilter({
          style: { display: this.state.displayFilter },
          placeholder: t("SoftwareCatalogs.FilterNamePlaceholder"),
        }),
        formatter: (name, softwareCatalog) => (
          <Link
            className="item-link"
            to={{
              pathname: `/softwareCatalogs/${softwareCatalog._id}`,
              state: {
                softwareCatalog,
              },
            }}
          >
            {name}
          </Link>
        ),
      },
      {
        dataField: "provider",
        text: t("SoftwareCatalogs.ColumnProvider"),
        formatter: this.getProviderValue,
        sort: true,
        filter: selectFilter({
          options: [
            { label: "CAMPLA", value: 0 },
            { label: "Chocolatey", value: 1 },
          ],
          style: { display: this.state.displayFilter },
          placeholder: t("SoftwareCatalogs.FilterProviderPlaceholder"),
        }),
      },
      {
        dataField: "updated_at",
        text: t("SoftwareCatalogs.ColumnUpdatedAt"),
        sort: true,
        formatter: (cell) => DateTimeHelper.formatDate(cell),
        filter: dateFilter({
          style: { display: this.state.displayFilter },
          defaultValue: { date: undefined, comparator: Comparator.EQ },
        }),
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
            <PaginationTableFilterToggle
                onFilterToggle={(e) => {
                  this.setState({
                    showFilters: e.currentTarget.checked,
                    displayFilter: e.currentTarget.checked ? "initial" : "none",
                  });
                }}
                showFilters={this.state.showFilters}
                tableId={this.tableId} />
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatExtraData: this.state.cloneItemId,
        formatter: (action, softwareCatalog) => {
          return (
            <SoftwareCatalogsActionCell
                item={softwareCatalog}
                cloneItemId={this.state.cloneItemId}
                onClone={() => this.handleClone(softwareCatalog)}
                onDelete={() =>
                this.setState({
                  showDeleteModal: true,
                  catalogToDeleteOrEdit: Object.assign({}, softwareCatalog),
                })
              }
            />
          );
        },
      },
    ];

    return (
      <div>
        <UnsavedChangesDialog
          show={this.state.showUnsavedChangesDialog}
          onHide={() => this.setState({ showUnsavedChangesDialog: false })}
          onSubmit={() => {
            this.setState({ showUnsavedChangesDialog: false });
            this.state.closeAction();
          }}
        />
        <DeleteDialog
          show={this.state.showDeleteModal}
          onHide={() =>
            this.setState({ showDeleteModal: false, dialogApiError: "" })
          }
          onSubmit={this.handleDeleteCatalog}
          dialogApiError={this.state.dialogApiError}
          title={t("SoftwareCatalogs.DeleteModal.Title")}
          body={t("SoftwareCatalogs.DeleteModal.Text", {
            softwareCatalogName: this.state.catalogToDeleteOrEdit?.name,
          })}
          rightButtonText={t("SoftwareCatalogs.DeleteModal.Confirm")}
          leftButtonText={t("DialogCancel")}
        />
        <CreateSoftwareCatalogDialog
          show={this.state.showCreateModal}
          onHide={this.onHideCreationDialog}
          onSubmit={this.handleCreateCatalog}
          dialogApiError={this.state.dialogApiError}
        />

        <Container className="mt-3">
          <Row className="pageTitle">
            <Col>
              <h2>{t("SoftwareCatalogs.Title")}</h2>
            </Col>
            <Col>
              <Button
                className="btn-green"
                style={{ float: "right" }}
                onClick={() => this.setState({ showCreateModal: true })}
              >
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </Col>
          </Row>
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row>
            <Col>
              <PaginationTable
                data={this.state.softwareCatalogs}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                ref={this.paginationTableRef}
                id={this.tableId}
                view="softwareCatalogs"
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  getProviderValue(type) {
    if (type) {
      return "Chocolatey";
    }
    return "CAMPLA";
  }
}

export default withTranslation()(SoftwareCatalogs);
